// 🔀 Composable pour la navigation conditionnelle
import { ref, computed } from 'vue';

export function useConditionalNavigation(conditionalStructure, testData) {
  const currentNodeId = ref(null);
  const pathTaken = ref([]); // Historique des nodes visités
  const nodeResponses = ref([]); // Réponses avec leurs scores
  const currentScore = ref(0); // Score cumulé
  const currentMaxScore = ref(0); // Score maximum cumulé

  /**
   * Initialise la navigation au startNode
   */
  const initializeNavigation = () => {
    if (!conditionalStructure.value || !conditionalStructure.value.startNode) {
      console.error('❌ ConditionalStructure ou startNode manquant');
      return false;
    }

    currentNodeId.value = conditionalStructure.value.startNode;
    pathTaken.value = [currentNodeId.value];
    nodeResponses.value = [];
    currentScore.value = 0;
    currentMaxScore.value = 0;

    console.log('🔀 Navigation initialisée au node:', currentNodeId.value);
    return true;
  };

  /**
   * Récupère le node courant depuis la structure
   */
  const getCurrentNode = computed(() => {
    if (!currentNodeId.value || !conditionalStructure.value) return null;

    const node = conditionalStructure.value.nodes.find(n => n.id === currentNodeId.value);
    console.log('📍 Current node:', node);
    return node;
  });

  /**
   * Récupère l'élément (interaction) associé au node courant
   * Retourne null pour les PageNodes car ils n'ont pas d'élément propre
   */
  const getCurrentElement = computed(() => {
    const node = getCurrentNode.value;
    if (!node || !node.data) return null;

    // Les PageNodes n'ont pas d'élément propre - ils contiennent des nested nodes
    if (node.type === 'page') {
      console.log('📄 Current node is a PageNode - no element');
      return null;
    }

    if (!node.data.elementId) return null;

    const element = testData.value.elements?.find(el => el.el_ID === node.data.elementId);
    console.log('🎯 Current element:', element);
    return element;
  });

  /**
   * Vérifie si le node courant est un PageNode
   */
  const isPageNode = computed(() => {
    const node = getCurrentNode.value;
    return node?.type === 'page';
  });

  /**
   * Récupère les nested nodes (enfants) d'un PageNode
   */
  const getPageChildren = computed(() => {
    if (!isPageNode.value || !conditionalStructure.value) return [];

    const pageNodeId = currentNodeId.value;
    const childNodes = conditionalStructure.value.nodes.filter(
      n => n.parentNode === pageNodeId
    );

    console.log('📄 PageNode children:', childNodes);
    return childNodes;
  });

  /**
   * Récupère les éléments (interactions) de tous les nested nodes d'une page
   */
  const getPageChildElements = computed(() => {
    const children = getPageChildren.value;
    if (!children || children.length === 0) return [];

    const childElements = children
      .map(childNode => {
        const element = testData.value.elements?.find(
          el => el.el_ID === childNode.data?.elementId
        );
        return element ? { node: childNode, element } : null;
      })
      .filter(Boolean);

    console.log('🎯 PageNode child elements:', childElements);
    return childElements;
  });

  /**
   * Vérifie si le node courant est un node de terminaison
   */
  const isTerminationNode = computed(() => {
    const node = getCurrentNode.value;
    return node?.type === 'termination' || node?.data?.isTermination === true;
  });

  /**
   * Récupère les données de terminaison
   */
  const getTerminationData = computed(() => {
    if (!isTerminationNode.value) return null;

    const node = getCurrentNode.value;
    return {
      nodeId: node.id,
      message: node.data.terminationMessage || 'Thank you for your participation.',
      bonus: node.data.terminationBonus || 0
    };
  });

  /**
   * Trouve le prochain node basé sur la réponse de l'étudiant
   * @param {Object} answer - Réponse de l'étudiant
   * @param {Number} score - Score obtenu pour cette réponse
   * @returns {String|null} - ID du prochain node ou null si erreur
   */
  const findNextNode = (answer, score) => {
    const node = getCurrentNode.value;
    if (!node) {
      console.error('❌ Pas de node courant');
      return null;
    }

    console.log('🔍 Finding next node for:', { nodeType: node.type, nodeData: node.data, answer, score });

    // Cas 1: Node de type "switcher" (invisible, redirection automatique basée sur le score)
    if (node.type === 'switcher') {
      console.log('🔀 Node SWITCHER détecté - évaluation des conditions');
      return evaluateSwitcherNode(node);
    }

    // Cas 2: Node de type "page" (PageNode avec nested nodes)
    if (node.type === 'page') {
      console.log('📄 Node PAGE détecté - navigation simple vers node suivant');
      return findNextNodeForPage(node);
    }

    // Cas 3: Pour les nodes de type "question" OU interaction types (choice, order, match, etc.)
    // Les nested nodes peuvent avoir directement le type de l'interaction au lieu de "question"
    const interactionTypes = ['choice', 'choiceBlock', 'textGaps', 'shortAnswer', 'order', 'match', 'makingPairs', 'hotSpot', 'message', 'textPuzzle'];

    if (node.type === 'question' || interactionTypes.includes(node.type)) {
      const edges = conditionalStructure.value.edges.filter(e => e.source === node.id);

      if (edges.length === 0) {
        console.error('❌ Node question/interaction sans edge sortant');
        return null;
      }

      // Vérifier si c'est un mode "score" (sourceHandle === "score")
      const isScoreMode = edges.length === 1 && edges[0].sourceHandle === 'score';

      if (isScoreMode || node.data.mode === 'score') {
        console.log('📊 Mode SCORE détecté - navigation vers switcher');
        return findNextNodeForScore(node);
      }

      // Si plusieurs edges avec sourceHandle définis (sauf "score") → mode "choice"
      const hasMultipleHandles = edges.some(e => e.sourceHandle && e.sourceHandle !== 'score');

      if (hasMultipleHandles || node.data.mode === 'choice') {
        console.log('🔀 Mode CHOICE détecté - navigation conditionnelle');
        return findNextNodeForChoice(node, answer);
      }

      // Sinon, node simple (message, etc.) avec une seule sortie
      console.log('📤 Node question/interaction simple → node suivant:', edges[0].target);
      return edges[0].target;
    }

    console.error('❌ Type de node non supporté:', node.type);
    return null;
  };

  /**
   * Trouve le prochain node pour un node de type "choice"
   * Match le sourceHandle de l'edge avec l'ID de la réponse sélectionnée
   */
  const findNextNodeForChoice = (node, answer) => {
    const edges = conditionalStructure.value.edges.filter(e => e.source === node.id);

    console.log('🔀 Edges sortants:', edges);
    console.log('📝 Answer:', answer);

    // Récupérer l'ID de la réponse sélectionnée
    let selectedOptionId = null;

    if (answer.selectedOptions && Array.isArray(answer.selectedOptions)) {
      // Pour choice multiple ou single
      selectedOptionId = answer.selectedOptions[0]; // Prendre la première réponse
    } else if (typeof answer.selectedOptions === 'string') {
      selectedOptionId = answer.selectedOptions;
    }

    console.log('🎯 Selected option ID:', selectedOptionId);

    // Trouver l'edge correspondant
    const matchingEdge = edges.find(edge => {
      const sourceHandle = edge.sourceHandle;
      console.log('🔗 Comparing edge sourceHandle:', sourceHandle, 'with selectedOptionId:', selectedOptionId);
      return sourceHandle === selectedOptionId;
    });

    if (!matchingEdge) {
      console.error('❌ Aucun edge trouvé pour la réponse:', selectedOptionId);
      return null;
    }

    console.log('✅ Edge trouvé:', matchingEdge);
    return matchingEdge.target;
  };

  /**
   * Trouve le prochain node pour un node de type "score"
   * Les nodes "score" pointent toujours vers un unique node "switcher"
   */
  const findNextNodeForScore = (node) => {
    const edges = conditionalStructure.value.edges.filter(e => e.source === node.id);

    if (edges.length === 0) {
      console.error('❌ Node score sans edge sortant');
      return null;
    }

    if (edges.length > 1) {
      console.warn('⚠️ Node score avec plusieurs edges, prise du premier');
    }

    // Le node score doit pointer vers un switcher
    const targetNodeId = edges[0].target;
    console.log('📊 Node score redirige vers switcher:', targetNodeId);

    return targetNodeId;
  };

  /**
   * Trouve le prochain node pour un PageNode
   * Les PageNodes ont toujours une seule sortie (pas de conditions)
   */
  const findNextNodeForPage = (node) => {
    const edges = conditionalStructure.value.edges.filter(e => e.source === node.id);

    if (edges.length === 0) {
      console.error('❌ PageNode sans edge sortant');
      return null;
    }

    if (edges.length > 1) {
      console.warn('⚠️ PageNode avec plusieurs edges, prise du premier');
    }

    // Le PageNode pointe vers le node suivant
    const targetNodeId = edges[0].target;
    console.log('📄 PageNode redirige vers:', targetNodeId);

    return targetNodeId;
  };

  /**
   * Évalue un node switcher et retourne le node de destination
   * Basé sur les conditions définies dans node.data.conditions[] et les edges sortants
   */
  const evaluateSwitcherNode = (switcherNode) => {
    const conditions = switcherNode.data.conditions;

    if (!conditions || !Array.isArray(conditions)) {
      console.error('❌ Node switcher sans conditions');
      return null;
    }

    console.log('🔀 Évaluation du switcher avec score:', currentScore.value);
    console.log('📋 Conditions:', conditions);

    // Récupérer les edges sortants du switcher
    const edges = conditionalStructure.value.edges.filter(e => e.source === switcherNode.id);
    console.log('🔗 Edges sortants:', edges);

    // Parcourir les conditions et trouver la première qui match
    for (const condition of conditions) {
      const { id, operator, value } = condition;
      const test = `${operator}${value}`; // Construire le test (ex: ">5", "<=10")

      console.log('🧪 Test d\'orientation:', {
        conditionId: id,
        operator,
        value,
        test,
        currentScore: currentScore.value,
        expression: `${currentScore.value} ${test}`
      });

      if (evaluateCondition(test, currentScore.value)) {
        console.log('✅ Condition matchée:', test);

        // Trouver l'edge correspondant à cette condition (via sourceHandle)
        const matchingEdge = edges.find(edge => edge.sourceHandle === id);

        if (!matchingEdge) {
          console.error('❌ Aucun edge trouvé pour la condition:', id);
          console.error('📋 Edges disponibles:', edges.map(e => ({
            id: e.id,
            sourceHandle: e.sourceHandle,
            target: e.target
          })));
          console.error('🔍 Condition ID recherchée:', id);
          continue;
        }

        console.log('✅ Edge trouvé → destination:', matchingEdge.target);
        return matchingEdge.target;
      } else {
        console.log('❌ Condition NON matchée:', test);
      }
    }

    console.error('❌ Aucune condition ne matche pour le switcher');
    return null;
  };

  /**
   * Évalue une condition textuelle (ex: ">0", "<=50", ">=10")
   * @param {String} test - Expression à évaluer (ex: ">0")
   * @param {Number} score - Score à tester
   * @returns {Boolean}
   */
  const evaluateCondition = (test, score) => {
    try {
      // Sécurité: vérifier que le test ne contient que des opérateurs valides
      const validOperators = /^(>|<|>=|<=|==|!=)\s*-?\d+(\.\d+)?$/;
      if (!validOperators.test(test)) {
        console.error('❌ Condition invalide:', test);
        return false;
      }

      // Construire l'expression JavaScript
      const expression = `${score} ${test}`;
      console.log('🧮 Évaluation:', expression);

      // Évaluer l'expression
      // eslint-disable-next-line no-eval
      const result = eval(expression);
      console.log('📊 Résultat:', result);

      return result;
    } catch (error) {
      console.error('❌ Erreur évaluation condition:', error);
      return false;
    }
  };

  /**
   * Navigue vers un node spécifique
   */
  const navigateToNode = (nodeId) => {
    if (!nodeId) {
      console.error('❌ NodeId invalide pour navigation');
      return false;
    }

    // Vérifier que le node existe
    const node = conditionalStructure.value.nodes.find(n => n.id === nodeId);
    if (!node) {
      console.error('❌ Node introuvable:', nodeId);
      return false;
    }

    currentNodeId.value = nodeId;
    pathTaken.value.push(nodeId);

    console.log('✅ Navigation vers node:', nodeId);
    console.log('📍 Path taken:', pathTaken.value);

    return true;
  };

  /**
   * Enregistre une réponse et met à jour les scores
   */
  const recordResponse = (nodeId, elementId, response, score, maxScore) => {
    // Trouver le node pour vérifier son mode
    const node = conditionalStructure.value.nodes.find(n => n.id === nodeId);
    const nodeType = node?.type;
    const nodeMode = node?.data?.mode;
    const nodeSubtype = node?.data?.subtype || node?.type;

    console.log('🔍 recordResponse - Node détails:', {
      nodeId: nodeId.substring(0, 8),
      nodeType,
      nodeSubtype,
      nodeMode,
      nodeModeType: typeof nodeMode,
      score,
      maxScore
    });

    // ⚠️ Skip scoring pour PageNodes - ce sont les nested nodes enfants qui comptent
    if (nodeType === 'page') {
      console.log('📄 PageNode response - aucun score enregistré (les nested nodes comptent)');
      return;
    }

    const responseRecord = {
      nodeId,
      elementId,
      response,
      score,
      maxScore,
      mode: nodeMode, // Enregistrer le mode pour analyse
      timestamp: new Date().toISOString()
    };

    nodeResponses.value.push(responseRecord);

    // ✅ Ne comptabiliser le score QUE si le node est en mode "score"
    if (nodeMode === 'score') {
      currentScore.value += score;
      currentMaxScore.value += maxScore;
      console.log('📝 Réponse enregistrée (MODE SCORE) :', responseRecord);
      console.log('📊 Score cumulé:', currentScore.value, '/', currentMaxScore.value);
    } else {
      console.log('📝 Réponse enregistrée (MODE OPTIONS ou undefined - pas de score):', {
        mode: nodeMode,
        modeType: typeof nodeMode,
        isNull: nodeMode === null,
        isUndefined: nodeMode === undefined
      });
      console.log('📊 Score cumulé inchangé:', currentScore.value, '/', currentMaxScore.value);
    }
  };

  /**
   * Calcule le score d'une réponse
   * (Simplifié - peut être enrichi avec logique plus complexe)
   */
  const calculateScore = (element, answer) => {
    // Priorité au maxScore de la réponse (calculé par le composant), sinon celui de l'élément
    const maxScore = answer.maxScore !== undefined ? answer.maxScore : (element.maxScore || 0);

    return {
      score: answer.score || 0,
      maxScore: maxScore
    };
  };

  return {
    // State
    currentNodeId,
    pathTaken,
    nodeResponses,
    currentScore,
    currentMaxScore,

    // Computed
    getCurrentNode,
    getCurrentElement,
    isTerminationNode,
    getTerminationData,
    isPageNode,
    getPageChildren,
    getPageChildElements,

    // Methods
    initializeNavigation,
    findNextNode,
    navigateToNode,
    recordResponse,
    calculateScore
  };
}
